import { fonts } from '@/theme/fonts';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface PaginationProps {
  totalItems: number;
  itemsPerPage?: number;
  currentPage: number; // 외부에서 현재 페이지를 받음
  onPageChange: (page: number) => void; // 페이지 변경 시 부모 호출
}

const Pagination = ({
  totalItems,
  itemsPerPage = 10,
  currentPage,
  onPageChange,
}: PaginationProps) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const groupSize = 10;
  const currentGroup = Math.floor((currentPage - 1) / groupSize);
  const startPage = currentGroup * groupSize + 1;
  const endPage = Math.min(startPage + groupSize - 1, totalPages);
  const { t } = useTranslation();

  const pages: number[] = [];
  for (let i = startPage; i <= endPage; i++) pages.push(i);

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onPageChange(page);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <PaginationWrap>
      <Button disabled={currentPage === 1} onClick={goToPrevPage}>
        {t('prev')}
      </Button>

      {pages.map((p) => (
        <PageButton
          key={p}
          data-active={p === currentPage}
          onClick={() => goToPage(p)}
        >
          {p}
        </PageButton>
      ))}

      <Button
        disabled={currentPage === totalPages || totalItems === 0}
        onClick={goToNextPage}
      >
        {t('next')}
      </Button>
    </PaginationWrap>
  );
};

export default Pagination;

const PaginationWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 40.5px;
`;

const Button = styled.button`
  padding: 10px 17.5px;
  background: white;
  font-family: ${fonts.family.poppins};
  font-weight: ${fonts.weight.semiBold};
  font-size: 15px;
  line-height: 120%;
  border: 1px solid #bebebe;
  border-radius: 5px;
  cursor: pointer;
  color: #000000;
  &:disabled {
    color: #b9b9b9;
    cursor: default;
  }
`;

const PageButton = styled(Button)`
  background: white;
  box-sizing: border-box;
  border: 1px solid #bebebe;
  color: #000000;
  &[data-active='true'] {
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
    background: #769bf7;
  }
`;
